import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { api } from '../../../api/api';
import { queryKeys } from '../../../api/queryKeys';
import { getRefreshToken } from '../../../helpers/localstorage-helper';
import type {
  NotificationCreatedPayload,
  NotificationsReadPayload,
} from '../../../types/notification.types';
import {
  getUnreadCount,
  markSeenInCache,
  prependNotification,
  refreshNotifications,
  setUnreadCount,
} from '../notificationsCache';
import { useNotificationsViewer } from './useNotificationsViewer';

const SOCKET_URL = process.env.NX_API_URL;

/**
 * Keeps the bell live: the `/notifications` socket, mounted once by the layout.
 *
 * The user is put in their own room at connect, so there is nothing to
 * subscribe to - the two events just arrive:
 *
 *   - `notification_created`: the new count, and the row on top of any list
 *     that is loaded. No toast - a push already announces it where push is
 *     on, and the badge is the in-app signal.
 *   - `notifications_read`: this user read one or all, here or on another
 *     tab or device. Carries only the count, so the lists are refetched -
 *     unless the count says this tab already applied it (see below).
 *
 * Recovery is the chat socket's, in miniature: the server hanging up and the
 * handshake refusing a stale token are both final to socket.io, so both are
 * retried here with a backoff, and every gap in coverage is closed by
 * refetching.
 */
export const useNotificationsSocket = () => {
  const queryClient = useQueryClient();
  const { userId, enabled } = useNotificationsViewer();
  const refreshToken = getRefreshToken();
  const socketRef = useRef<Socket | null>(null);
  const active = enabled && !!SOCKET_URL;

  useEffect(() => {
    if (!active || !refreshToken) return undefined;

    const socket = io(SOCKET_URL, {
      path: '/notifications',
      transports: ['websocket'],
      auth: { authorization: refreshToken },
    });
    socketRef.current = socket;

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const retryAfter = (attempt: number, run: () => void) => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(
        run,
        Math.min(2000 * 2 ** (attempt - 1), 30000)
      );
    };

    /* Every connect after the first has a gap behind it - a dropped network,
       a server restart, a socket rebuilt for a rotated token - and whatever
       was said in it is gone. */
    let connectedBefore = false;
    let serverDrops = 0;
    let authRejections = 0;
    socket.on('connect', () => {
      serverDrops = 0;
      authRejections = 0;
      if (connectedBefore) refreshNotifications(queryClient, userId);
      connectedBefore = true;
    });

    socket.on(
      'notification_created',
      ({ notification, unreadCount }: NotificationCreatedPayload) => {
        setUnreadCount(queryClient, userId, unreadCount);
        prependNotification(queryClient, userId, notification);
      }
    );

    socket.on(
      'notifications_read',
      ({ unreadCount }: NotificationsReadPayload) => {
        const before = getUnreadCount(queryClient, userId);
        setUnreadCount(queryClient, userId, unreadCount);
        if (unreadCount === 0) {
          // Everything is read, which needs no fetch to show.
          markSeenInCache(queryClient, userId, null);
          return;
        }
        /* The echo of this tab's own click: the optimistic update already
         moved the badge to this number, so there is nothing to learn. */
        if (before === unreadCount) return;
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.listAll(userId),
        });
      }
    );

    /* A deliberate hang-up (a deploy, a gateway restart) is final to
       socket.io, which would leave the bell silently dead until a reload. */
    socket.on('disconnect', (reason: string) => {
      if (reason !== 'io server disconnect') return;
      serverDrops += 1;
      retryAfter(serverDrops, () => socket.connect());
    });

    /* `Unauthorized` is the handshake refusing the refresh token. Ask the auth
       endpoint about it: success means the token in hand was stale (replaced
       by a login in another tab) and a reconnect with the current one sticks;
       failure means the session is over and the interceptor has already
       signed the user out. */
    socket.on('connect_error', (error: Error) => {
      if (error.message !== 'Unauthorized') return;
      authRejections += 1;
      retryAfter(authRejections, async () => {
        try {
          await api.post('/v1/auth/refresh');
        } catch {
          return;
        }
        const freshest = getRefreshToken();
        if (!freshest) return;
        socket.auth = { authorization: freshest };
        socket.connect();
      });
    });

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [active, refreshToken, userId, queryClient]);

  /* A phone that locked or backgrounded the tab comes back with a socket the
     server dropped long ago, and socket.io can take most of a minute to
     notice. Looked at again with a dead socket, ask the server instead. */
  useEffect(() => {
    if (!active) return undefined;

    const catchUpIfBlind = () => {
      if (document.visibilityState !== 'visible') return;
      if (socketRef.current?.connected) return;
      refreshNotifications(queryClient, userId);
    };

    document.addEventListener('visibilitychange', catchUpIfBlind);
    window.addEventListener('online', catchUpIfBlind);
    return () => {
      document.removeEventListener('visibilitychange', catchUpIfBlind);
      window.removeEventListener('online', catchUpIfBlind);
    };
  }, [active, userId, queryClient]);
};
